import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { List, Loader, Header, Icon, Label, Button, Input, Dropdown } from 'semantic-ui-react';
import { formatDistanceToNow, format as formatDate, isValid } from 'date-fns';
import './ConversationList.css'; // Reuse conversation list styling


/**
 * Notification type icon mapping
 */
const NOTIFICATION_TYPE_CONFIG = {
  'NewApplication': {
    icon: 'user plus',
    color: 'blue',
    label: 'New Application',
    navigateTo: (notification) => `/applications/${notification.about?.uuid || notification.aboutId || notification.applicationId}`
  },
  'ApplicationAccepted': {
    icon: 'check circle',
    color: 'green',
    label: 'Application Accepted',
    navigateTo: (notification) => `/applications/${notification.about?.uuid || notification.aboutId || notification.applicationId}`
  },
  'ApplicationRejected': {
    icon: 'times circle',
    color: 'red',
    label: 'Application Rejected',
    navigateTo: (notification) => `/applications/${notification.about?.uuid || notification.aboutId || notification.applicationId}`
  },
  'ApplicationStatusUpdate': {
    icon: 'exchange',
    color: 'orange',
    label: 'Status Update',
    navigateTo: (notification) => `/applications/${notification.about?.uuid || notification.aboutId || notification.applicationId}`
  },
  'default': {
    icon: 'bell',
    color: 'grey',
    label: 'Notification',
    navigateTo: () => null
  }
};

/**
 * Get config for a notification type
 */
const getTypeConfig = (type) => {
  return NOTIFICATION_TYPE_CONFIG[type] || NOTIFICATION_TYPE_CONFIG['default'];
};

/**
 * Format timestamp to relative time (e.g. "2 hours ago", "3 days ago")
 */
const formatTime = (timestamp) => {
  try {
    const date = new Date(timestamp);
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (error) {
    return 'Recently';
  }
};

/**
 * Format timestamp to a concise absolute date for display.
 * Examples: "Apr 16, 2026" or "Apr 16, 2026 2:30 PM" (with time = true).
 */
const formatAbsoluteDate = (timestamp, { withTime = false } = {}) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (!isValid(date)) return '';
  try {
    return withTime
      ? formatDate(date, 'MMM d, yyyy h:mm a')
      : formatDate(date, 'MMM d, yyyy');
  } catch (e) {
    return '';
  }
};

/**
 * Produce a set of date-related search tokens for a timestamp so users can
 * type whatever format feels natural and still match — e.g. "apr 16",
 * "April 16", "4/16/2026", "2026-04-16", "2026", etc.
 * Returns an already-lowercased, space-joined string ready to concatenate
 * into the search haystack.
 */
const buildDateSearchTokens = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (!isValid(date)) return '';
  try {
    const tokens = [
      formatDate(date, 'yyyy-MM-dd'),        // 2026-04-16
      formatDate(date, 'M/d/yyyy'),          // 4/16/2026
      formatDate(date, 'MM/dd/yyyy'),        // 04/16/2026
      formatDate(date, 'MMM d, yyyy'),       // Apr 16, 2026
      formatDate(date, 'MMMM d, yyyy'),      // April 16, 2026
      formatDate(date, 'MMM d'),             // Apr 16
      formatDate(date, 'MMMM d'),            // April 16
      formatDate(date, 'MMMM'),              // April (bare month name)
      formatDate(date, 'MMM'),               // Apr
      formatDate(date, 'yyyy'),              // 2026
      formatDate(date, 'EEEE'),              // Thursday (day-of-week name)
      formatDate(date, 'EEE'),               // Thu
      formatDistanceToNow(date, { addSuffix: true }) // "2 hours ago" etc.
    ];
    return tokens.join(' ').toLowerCase();
  } catch (e) {
    return '';
  }
};


/**
 * Single Notification Item
 */
const NotificationItem = ({ notification, onMarkRead, onNavigate }) => {
  const config = getTypeConfig(notification.type);
  const isUnread = !notification.isRead;

  const handleClick = () => {
    // Mark as read if unread
    if (isUnread && onMarkRead) {
      onMarkRead(notification.uuid);
    }
    // Navigate to related entity
    if (onNavigate) {
      const path = config.navigateTo(notification);
      if (path) {
        onNavigate(path);
      }
    }
  };

  return (
    <List.Item
      className={`conversation-preview ${isUnread ? 'selected' : ''}`}
      onClick={handleClick}
      style={{ cursor: 'pointer' }}
    >
      <div className="conversation-preview-content">
        <div className="conversation-header">
          <div className="participant-info">
            <div className="participant-name" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon name={config.icon} color={config.color} />
              <span style={{ fontWeight: isUnread ? 'bold' : 'normal', whiteSpace: 'pre-line' }}>
                {notification.message || 'System notification'}
              </span>
            </div>
            <div
              className="conversation-meta"
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}
            >
              <span
                className="last-message-time"
                title={formatAbsoluteDate(notification.createdAt, { withTime: true })}
              >
                {formatTime(notification.createdAt)}
              </span>
              {notification.createdAt && (
                <span
                  className="notification-absolute-date"
                  style={{ fontSize: '0.72rem', color: '#8899a6' }}
                >
                  {formatAbsoluteDate(notification.createdAt)}
                </span>
              )}
            </div>

          </div>

          <div className="conversation-indicators" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {isUnread && (
              <Label circular color="orange" size="mini" empty />
            )}
          </div>
        </div>

        <div className="latest-message">
          <div className="message-context" style={{ marginTop: '4px' }}>
            <Label size="mini" color="orange" basic>
              <Icon name="cog" />
              {notification.source || notification.channel || 'System'}
            </Label>
            <Label size="mini" basic>
              {config.label}
            </Label>
          </div>
        </div>

      </div>
    </List.Item>
  );
};

/**
 * Build the dropdown options for the "type" filter, using only types that
 * actually appear in the current notifications array. "All types" is always
 * first. Uses NOTIFICATION_TYPE_CONFIG for friendly labels and falls back to
 * the raw type string for unknown entries so nothing gets silently hidden.
 */
const buildTypeOptions = (notifications) => {
  const seen = new Set();
  notifications.forEach((n) => {
    if (n?.type) seen.add(n.type);
  });

  const options = [{ key: '__all__', value: '__all__', text: 'All types', icon: 'list' }];
  Array.from(seen)
    .sort()
    .forEach((type) => {
      const cfg = NOTIFICATION_TYPE_CONFIG[type] || NOTIFICATION_TYPE_CONFIG['default'];
      options.push({
        key: type,
        value: type,
        text: cfg.label || type,
        icon: cfg.icon
      });
    });
  return options;
};

/**
 * NotificationList Component
 * Displays a list of system notifications within the MessageCenter
 * Follows the same visual patterns as ConversationList
 */
const NotificationList = ({
  notifications = [],
  loading = false,
  onMarkRead,
  onMarkAllRead,
  onClose
}) => {
  const navigate = useNavigate();

  // Search header state (client-side filters)
  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState('__all__');

  const typeOptions = useMemo(
    () => buildTypeOptions(notifications),
    [notifications]
  );

  // Apply both filters. Text match runs against the notification message plus
  // the friendly type label (e.g. typing "accepted" matches ApplicationAccepted
  // even if the message text doesn't contain that word).
  const filteredNotifications = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    return notifications.filter((n) => {
      if (typeFilter !== '__all__' && n.type !== typeFilter) return false;
      if (!needle) return true;
      const cfg = getTypeConfig(n.type);
      // Include the user-visible "source" badge (falls back to "System") so it
      // becomes searchable as the backend eventually emits non-System sources.
      const source = n.source || n.channel || 'System';
      // Pre-expanded date tokens (yyyy-MM-dd, "Apr 16", "April 16, 2026",
      // "4/16/2026", "2026", weekday names, "2 hours ago", etc.) so typing any
      // reasonable date phrase matches this notification.
      const dateTokens = buildDateSearchTokens(n.createdAt);
      const haystack = [
        n.message || '',
        n.type || '',
        cfg.label || '',
        source,
        dateTokens
      ].join(' ').toLowerCase();
      return haystack.includes(needle);


    });
  }, [notifications, searchText, typeFilter]);

  const handleNavigate = (path) => {
    if (onClose) {
      onClose();
    }
    navigate(path);
  };

  const handleClearSearch = () => {
    setSearchText('');
    setTypeFilter('__all__');
  };

  if (loading) {
    return (
      <div className="conversation-list-loading">
        <Loader active inline="centered" />
        <p>Loading notifications...</p>
      </div>
    );
  }

  // Truly empty (no notifications at all) — show the existing empty state.
  if (notifications.length === 0) {
    return (
      <div className="conversation-list-empty">
        <Icon name="bell outline" size="large" />
        <Header as="h4">No notifications yet</Header>
        <p>System notifications will appear here when events occur, such as new applications or status changes.</p>
      </div>
    );
  }

  const unreadCount = notifications.filter(n => !n.isRead).length;
  const isFiltered = searchText.trim() !== '' || typeFilter !== '__all__';

  return (
    <div className="conversation-list-container">
      <div className="conversation-list-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Header as="h4" style={{ margin: 0 }}>
          <Icon name="bell" />
          Notifications ({isFiltered ? `${filteredNotifications.length} / ${notifications.length}` : notifications.length})
        </Header>
        {unreadCount > 0 && onMarkAllRead && (
          <Button
            size="mini"
            basic
            color="orange"
            onClick={onMarkAllRead}
            content={`Mark all read (${unreadCount})`}
            icon="check"
          />
        )}
      </div>

      {/* Search header: free-text search + type filter */}
      <div
        className="notification-search-header"
        style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
          padding: '8px 12px',
          borderBottom: '1px solid #e1e8ed',
          background: '#fafbfc',
          flexWrap: 'wrap'
        }}
      >
        <Input
          icon="search"
          iconPosition="left"
          placeholder="Search notifications..."
          value={searchText}
          onChange={(_, { value }) => setSearchText(value)}
          size="small"
          style={{ flex: '1 1 180px', minWidth: 0 }}
        />
        <Dropdown
          selection
          options={typeOptions}
          value={typeFilter}
          onChange={(_, { value }) => setTypeFilter(value)}
          style={{ flex: '0 0 220px', width: '220px', minWidth: '220px' }}
        />

        {isFiltered && (
          <Button
            basic
            size="mini"
            icon="close"
            title="Clear filters"
            onClick={handleClearSearch}
          />
        )}
      </div>

      {filteredNotifications.length === 0 ? (
        <div className="conversation-list-empty" style={{ padding: '24px 16px' }}>
          <Icon name="search" size="large" />
          <Header as="h4">No matches</Header>
          <p>No notifications match your search. Try a different term or clear the filters.</p>
          <Button basic size="tiny" onClick={handleClearSearch} icon="close" content="Clear filters" />
        </div>
      ) : (
        <List className="conversation-list" divided relaxed>
          {filteredNotifications.map((notification) => (
            <NotificationItem
              key={notification.uuid}
              notification={notification}
              onMarkRead={onMarkRead}
              onNavigate={handleNavigate}
            />
          ))}
        </List>
      )}
    </div>
  );
};

export default NotificationList;
